import { useState, useEffect, useCallback } from 'react';
import { DEFAULT_COUNTRIES, DEFAULT_CITIES_BY_COUNTRY } from '../lib/countries';

/**
 * Merge API countries with defaults. API data takes precedence.
 */
const mergeCountries = (apiCountries, defaults) => {
    const apiCodes = new Set(apiCountries.map(c => c.code));
    const merged = [...apiCountries];

    for (const def of defaults) {
        if (!apiCodes.has(def.code)) {
            const defaultCities = DEFAULT_CITIES_BY_COUNTRY[def.code] || [];
            merged.push({ ...def, cities: defaultCities });
        }
    }

    for (const country of merged) {
        if ((!country.cities || country.cities.length === 0) && DEFAULT_CITIES_BY_COUNTRY[country.code]) {
            country.cities = DEFAULT_CITIES_BY_COUNTRY[country.code];
        }
    }

    return merged.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
};

export const useGeographicData = () => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetchCountries = async () => {
            let apiCountries = [];
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geographic/countries`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        apiCountries = data.delivery_countries || [];
                    }
                }
            } catch (error) {
                console.error('Erreur récupération pays:', error);
            }

            if (!cancelled) {
                const merged = mergeCountries(apiCountries, DEFAULT_COUNTRIES);
                setCountries(merged);
                setLoading(false);
            }
        };

        fetchCountries();
        return () => { cancelled = true; };
    }, []);

    const getCitiesForCountry = useCallback((countryCode) => {
        if (!countryCode) return [];
        const country = countries.find(c => c.code === countryCode || c.name === countryCode);
        return country?.cities || [];
    }, [countries]);

    return { countries, loading, getCitiesForCountry };
};
